import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { AGENT_HUES, RAMP_DAY, RAMP_TIME } from "./sceneTheme";
import type { SlotProjection } from "./slotProjection";

interface Props {
  projection: SlotProjection;
}

/**
 * Four metrics in one strip.
 *
 * This replaced a 2×2 grid of full stat cards: the column has to fit a viewport
 * before sticky positioning means anything, and 200px of stat cards was most of
 * the overflow. Each metric carries the hue of the thing it counts — colour as
 * identification, not decoration — and flashes when its own value moves, so the
 * operator learns which setting drives which number.
 */
export function SlotPreviewStats({ projection }: Props) {
  const next = projection.nextSlot;

  return (
    <div className="grid shrink-0 grid-cols-4 gap-px border-b border-bridge-border bg-bridge-border">
      <Metric label="Slots" value={projection.weeklySlots} tone={AGENT_HUES[0]} hint="This week" />
      <Metric
        label="Capacity"
        value={projection.weeklyCapacity}
        tone={RAMP_DAY[3]}
        hint="This week"
      />
      <Metric
        label="Agents"
        value={projection.assignedUsers}
        tone={AGENT_HUES[2]}
        hint="In rotation"
      />
      <Metric
        label="Next"
        tone={RAMP_TIME[3]}
        text={
          next
            ? next.start.toLocaleDateString(undefined, { weekday: "short" }) +
              " " +
              next.start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
            : "—"
        }
        hint={next?.user?.name ?? "No bookable slot"}
      />
    </div>
  );
}

function Metric({
  label,
  value,
  text,
  hint,
  tone,
}: {
  label: string;
  value?: number;
  text?: string;
  hint: string;
  tone: string;
}) {
  const flash = useValueFlash(value ?? text);

  return (
    <div className="relative overflow-hidden bg-white px-4 py-3.5">
      <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: tone }} />
      <p className="font-mono text-[10.5px] uppercase tracking-[.11em] text-bridge-subtle">
        {label}
      </p>
      <p
        className={cn(
          "mt-0.5 font-bold tabular-nums tracking-tight text-bridge-fg transition-colors",
          text ? "text-[14px]" : "text-[22px]",
          flash && "text-bridge-accent",
        )}
      >
        {text ?? (value ?? 0).toLocaleString()}
      </p>
      <p className="truncate text-[11.5px] text-bridge-subtle">{hint}</p>
    </div>
  );
}

/** True for a beat after the value changes, so only what moved lights up. */
function useValueFlash(value: number | string | undefined): boolean {
  const [on, setOn] = useState(false);
  const previous = useRef(value);

  useEffect(() => {
    if (previous.current === value) return;
    previous.current = value;
    setOn(true);
    const id = window.setTimeout(() => setOn(false), 620);
    return () => window.clearTimeout(id);
  }, [value]);

  return on;
}
